import { useMemo, useState } from 'react';
import { Bike, Boxes, ChevronDown, ChevronUp, Store, UserMinus, UserPlus } from 'lucide-react';
import { OrderStatus } from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { usePuede } from '@/lib/permisos';
import { cn, desdeHace, formatCOP, formatDistance } from '@/lib/cn';
import {
  useAsignarManual,
  useDomiciliariosParaAsignar,
  useLiberarManual,
  useLoteAdmin,
  useLotesAdmin,
  useNoAliadosAdmin,
  type EnvioManual,
  type LoteAdmin,
} from '@/lib/queries';
import { Boton, Cargando, ErrorCarga, Seleccion, Vacio } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

const ESTADO: Record<string, { texto: string; clase: string }> = {
  [OrderStatus.PLACED]: { texto: 'Sin recoger', clase: 'bg-kick-light text-kick-gray' },
  [OrderStatus.PICKED_UP]: { texto: 'Recogido', clase: 'bg-violet-100 text-violet-800' },
  [OrderStatus.ON_THE_WAY]: { texto: 'En camino', clase: 'bg-blue-100 text-blue-800' },
  [OrderStatus.DELIVERED]: { texto: 'Entregado', clase: 'bg-emerald-100 text-emerald-800' },
  [OrderStatus.CANCELLED]: { texto: 'Cancelado', clase: 'bg-red-100 text-red-800' },
  [OrderStatus.REJECTED]: { texto: 'Rechazado', clase: 'bg-red-100 text-red-800' },
};

/**
 * Asignación manual.
 *
 * Lo que el motor no reparte: los lotes de envíos masivos de los comercios y
 * los pedidos a comercios no aliados. Aquí se ve qué espera, se elige a quién
 * y se puede repartir, quitar o cambiar sin más regla que el criterio de quien
 * mira la lista —el tope de pedidos simultáneos no aplica—.
 */
export function Manuales() {
  const [pestana, setPestana] = useState<'lotes' | 'no-aliados'>('lotes');
  const [todos, setTodos] = useState(false);

  return (
    <div className="mx-auto max-w-6xl">
      <h1 className="text-3xl text-kick-dark">Asignación manual</h1>
      <p className="mt-1 text-sm text-kick-gray">
        Lotes de envíos de comercios y pedidos a comercios no aliados. Tú decides quién los lleva.
      </p>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2" role="tablist">
          <Pestana activa={pestana === 'lotes'} onClick={() => setPestana('lotes')} icono={<Boxes className="h-4 w-4" />}>
            Lotes de comercios
          </Pestana>
          <Pestana
            activa={pestana === 'no-aliados'}
            onClick={() => setPestana('no-aliados')}
            icono={<Store className="h-4 w-4" />}
          >
            Comercios no aliados
          </Pestana>
        </div>
        <label className="flex items-center gap-2 text-sm text-kick-gray">
          <input type="checkbox" checked={todos} onChange={(e) => setTodos(e.target.checked)} />
          Ver también los terminados
        </label>
      </div>

      {pestana === 'lotes' ? <Lotes todos={todos} /> : <NoAliados todos={todos} />}
    </div>
  );
}

function Pestana({
  activa,
  onClick,
  icono,
  children,
}: {
  activa: boolean;
  onClick: () => void;
  icono: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={activa}
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-kick border px-3 py-2 text-sm font-semibold',
        activa ? 'border-kick-red bg-red-50 text-kick-red' : 'border-kick-gray/20 text-kick-gray',
      )}
    >
      {icono}
      {children}
    </button>
  );
}

// ── Lotes ─────────────────────────────────────────────────

function Lotes({ todos }: { todos: boolean }) {
  const lotes = useLotesAdmin(todos);
  const [abierto, setAbierto] = useState<string | null>(null);

  if (lotes.isLoading) return <Cargando texto="Cargando los lotes…" />;
  if (lotes.isError) {
    return <ErrorCarga mensaje={getErrorMessage(lotes.error)} onReintentar={() => void lotes.refetch()} />;
  }

  const lista = lotes.data ?? [];
  if (lista.length === 0) {
    return (
      <div className="mt-6">
        <Vacio
          icono={<Boxes className="h-6 w-6" />}
          titulo="Ningún lote esperando"
          descripcion="Cuando un comercio arme envíos masivos, aparecen aquí para repartirlos."
        />
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3">
      {lista.map((lote) => (
        <article key={lote.id} className="card-kick p-4">
          <button
            type="button"
            className="flex w-full flex-wrap items-center gap-3 text-left"
            onClick={() => setAbierto((a) => (a === lote.id ? null : lote.id))}
            aria-expanded={abierto === lote.id}
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-kick-light text-kick-dark">
              <Boxes className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-semibold text-kick-ink">{lote.merchantName}</span>
              <span className="block text-sm text-kick-gray">
                {lote.total} envíos · {formatCOP(lote.monto)} · {desdeHace(lote.createdAt)}
                {lote.notes ? ` · ${lote.notes}` : ''}
              </span>
            </span>
            <Cuentas lote={lote} />
            {abierto === lote.id ? (
              <ChevronUp className="h-4 w-4 text-kick-gray" />
            ) : (
              <ChevronDown className="h-4 w-4 text-kick-gray" />
            )}
          </button>
          {abierto === lote.id && <DetalleLote id={lote.id} />}
        </article>
      ))}
    </div>
  );
}

function Cuentas({ lote }: { lote: LoteAdmin }) {
  return (
    <span className="flex flex-wrap gap-1.5 text-xs">
      {lote.sinAsignar > 0 && (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">
          {lote.sinAsignar} sin asignar
        </span>
      )}
      {lote.enCurso > 0 && (
        <span className="rounded-full bg-blue-100 px-2 py-0.5 font-semibold text-blue-800">
          {lote.enCurso} en curso
        </span>
      )}
      {lote.entregados > 0 && (
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800">
          {lote.entregados} entregados
        </span>
      )}
      {lote.cancelados > 0 && (
        <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-800">
          {lote.cancelados} cancelados
        </span>
      )}
    </span>
  );
}

function DetalleLote({ id }: { id: string }) {
  const lote = useLoteAdmin(id);
  if (lote.isLoading || !lote.data) return <Cargando texto="Abriendo el lote…" />;

  return (
    <div className="mt-4 border-t border-kick-gray/10 pt-4">
      <p className="text-sm text-kick-gray">
        Se recogen en <strong className="text-kick-ink">{lote.data.merchantAddress}</strong>.
      </p>
      <TablaEnvios envios={lote.data.envios} />
    </div>
  );
}

// ── Comercios no aliados ──────────────────────────────────

function NoAliados({ todos }: { todos: boolean }) {
  const lista = useNoAliadosAdmin(todos);

  if (lista.isLoading) return <Cargando texto="Cargando los pedidos…" />;
  if (lista.isError) {
    return <ErrorCarga mensaje={getErrorMessage(lista.error)} onReintentar={() => void lista.refetch()} />;
  }

  const envios = lista.data ?? [];
  if (envios.length === 0) {
    return (
      <div className="mt-6">
        <Vacio
          icono={<Store className="h-6 w-6" />}
          titulo="Ningún pedido a comercios no aliados"
          descripcion="Cuando un cliente pida algo de una tienda que no está en la plataforma, aparece aquí."
        />
      </div>
    );
  }

  return (
    <div className="card-kick mt-4 p-4">
      <TablaEnvios envios={envios} conCliente />
    </div>
  );
}

// ── La tabla con selección y asignación ───────────────────

function TablaEnvios({ envios, conCliente }: { envios: EnvioManual[]; conCliente?: boolean }) {
  const puede = usePuede();
  const avisos = useAvisos();
  const domiciliarios = useDomiciliariosParaAsignar();
  const asignar = useAsignarManual();
  const liberar = useLiberarManual();
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [courierId, setCourierId] = useState('');

  const gestiona = puede('manuales', 'gestionar');
  const asignables = useMemo(
    () => envios.filter((e) => e.status === OrderStatus.PLACED),
    [envios],
  );
  const elegidos = envios.filter((e) => seleccion.has(e.id));
  const elegidosAsignados = elegidos.filter((e) => e.courierId);

  const alternar = (id: string): void =>
    setSeleccion((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const limpiar = (): void => setSeleccion(new Set());

  const hacerAsignacion = (): void => {
    if (!courierId || elegidos.length === 0) return;
    asignar.mutate(
      { orderIds: elegidos.map((e) => e.id), courierId },
      {
        onSuccess: (r) => {
          const quien = domiciliarios.data?.find((d) => d.id === courierId)?.fullName ?? 'el domiciliario';
          avisos.exito(
            `${r.asignados} envío${r.asignados === 1 ? '' : 's'} asignado${r.asignados === 1 ? '' : 's'} a ${quien}` +
              (r.omitidos.length ? ` · ${r.omitidos.length} sin cambio` : ''),
          );
          limpiar();
        },
        onError: (e) => avisos.error(getErrorMessage(e)),
      },
    );
  };

  const hacerLiberacion = (): void => {
    if (elegidosAsignados.length === 0) return;
    liberar.mutate(
      { orderIds: elegidosAsignados.map((e) => e.id) },
      {
        onSuccess: (r) => {
          avisos.exito(`${r.liberados} envío${r.liberados === 1 ? '' : 's'} sin asignación`);
          limpiar();
        },
        onError: (e) => avisos.error(getErrorMessage(e)),
      },
    );
  };

  return (
    <div>
      {gestiona && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-kick bg-kick-light p-3">
          <button
            type="button"
            className="text-sm font-semibold text-kick-red hover:underline"
            onClick={() =>
              setSeleccion(new Set(asignables.filter((e) => !e.courierId).map((e) => e.id)))
            }
          >
            Seleccionar sin asignar
          </button>
          <span className="text-sm text-kick-gray">·</span>
          <button type="button" className="text-sm font-semibold text-kick-gray hover:underline" onClick={limpiar}>
            Limpiar
          </button>
          <span className="ml-auto text-sm text-kick-ink">
            {elegidos.length} seleccionado{elegidos.length === 1 ? '' : 's'}
          </span>
          <Seleccion
            value={courierId}
            onChange={(e) => setCourierId(e.target.value)}
            aria-label="Domiciliario"
            className="w-72"
          >
            <option value="">Elige un domiciliario…</option>
            {(domiciliarios.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.fullName} · {d.status === 'AVAILABLE' ? 'disponible' : d.status === 'OFFLINE' ? 'fuera de turno' : 'ocupado'}
                {d.activeOrders > 0 ? ` · lleva ${d.activeOrders}` : ''}
                {d.enLote ? ' · en lote' : ''}
                {d.balance <= 0 ? ' · sin saldo' : ''}
              </option>
            ))}
          </Seleccion>
          <Boton
            className="px-3 py-1.5 text-xs"
            icono={<UserPlus className="h-3.5 w-3.5" />}
            cargando={asignar.isPending}
            disabled={!courierId || elegidos.length === 0}
            onClick={hacerAsignacion}
          >
            Asignar {elegidos.length > 0 ? `(${elegidos.length})` : ''}
          </Boton>
          <Boton
            variante="secundario"
            className="px-3 py-1.5 text-xs"
            icono={<UserMinus className="h-3.5 w-3.5" />}
            cargando={liberar.isPending}
            disabled={elegidosAsignados.length === 0}
            onClick={hacerLiberacion}
          >
            Quitar asignación
          </Boton>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-kick-gray">
            <tr>
              {gestiona && <th className="w-8 py-2" />}
              <th className="py-2 pr-3">Envío</th>
              {conCliente && <th className="py-2 pr-3">Comercio y compra</th>}
              <th className="py-2 pr-3">Entregar a</th>
              <th className="py-2 pr-3">Distancia</th>
              <th className="py-2 pr-3">Precio</th>
              <th className="py-2 pr-3">Estado</th>
              <th className="py-2">Domiciliario</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-kick-gray/10">
            {envios.map((e) => {
              const estado = ESTADO[e.status] ?? { texto: e.status, clase: 'bg-kick-light text-kick-gray' };
              const seleccionable = gestiona && e.status === OrderStatus.PLACED;
              return (
                <tr key={e.id} className={cn(seleccion.has(e.id) && 'bg-red-50/40')}>
                  {gestiona && (
                    <td className="py-2">
                      <input
                        type="checkbox"
                        aria-label={`Seleccionar ${e.code}`}
                        disabled={!seleccionable}
                        checked={seleccion.has(e.id)}
                        onChange={() => alternar(e.id)}
                      />
                    </td>
                  )}
                  <td className="py-2 pr-3">
                    <span className="font-mono font-semibold text-kick-ink">{e.code}</span>
                    {e.batchPosition && (
                      <span className="ml-1 text-xs text-kick-gray">#{e.batchPosition}</span>
                    )}
                    <span className="block text-xs text-kick-gray">{e.description}</span>
                  </td>
                  {conCliente && (
                    <td className="py-2 pr-3">
                      <span className="block font-semibold text-kick-ink">{e.storeName ?? e.pickupName}</span>
                      <span className="block text-xs text-kick-gray">{e.pickupAddress.addressLine}</span>
                      {e.purchaseDetails && (
                        <span className="block text-xs text-kick-ink">{e.purchaseDetails}</span>
                      )}
                      {e.purchaseAmount !== null && e.purchaseAmount !== undefined && (
                        <span className="block text-xs text-kick-gray">
                          compra aprox. {formatCOP(e.purchaseAmount)} · la paga el cliente al recibir
                        </span>
                      )}
                      <span className="block text-xs text-kick-gray">
                        Pide: {e.customerName}
                        {e.customerPhone ? ` · ${e.customerPhone}` : ''}
                      </span>
                    </td>
                  )}
                  <td className="py-2 pr-3">
                    <span className="block text-kick-ink">{e.recipientName}</span>
                    <span className="block text-xs text-kick-gray">
                      {e.deliveryAddress.addressLine}
                      {e.deliveryAddress.details ? ` · ${e.deliveryAddress.details}` : ''}
                    </span>
                    <span className="block text-xs text-kick-gray">{e.recipientPhone}</span>
                  </td>
                  <td className="py-2 pr-3 text-kick-gray">{formatDistance(e.distanceMeters)}</td>
                  <td className="py-2 pr-3 font-semibold text-kick-ink">{formatCOP(e.total)}</td>
                  <td className="py-2 pr-3">
                    <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', estado.clase)}>
                      {estado.texto}
                    </span>
                  </td>
                  <td className="py-2">
                    {e.courierName ? (
                      <span className="flex items-center gap-1 text-kick-ink">
                        <Bike className="h-3.5 w-3.5 text-kick-gray" />
                        {e.courierName}
                      </span>
                    ) : (
                      <span className="text-xs text-amber-700">Sin asignar</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
